import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User as UserIcon, LogOut, Users, Shield } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { getMyCharacters, Character } from '@/api/characterApi';
import ClassIconSimple from './ClassIconSimple';

interface UserProfileDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  characters?: Character[];
}

/**
 * 사용자 프로필 Dialog
 * 사용자 정보, 등록된 캐릭터, 로그아웃 등을 제공
 */
export function UserProfileDialog({ children, open: controlledOpen, onOpenChange, characters: externalCharacters }: UserProfileDialogProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [internalCharacters, setInternalCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // Controlled mode: use external open state, Uncontrolled mode: use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Use external characters if provided, otherwise use internal state
  const characters = externalCharacters || internalCharacters;

  // Dialog가 열릴 때 캐릭터 정보 로드 (외부 캐릭터가 없을 때만)
  useEffect(() => {
    if (open && !externalCharacters) {
      loadCharacters();
    }
  }, [open, externalCharacters]);

  const loadCharacters = async () => {
    setLoading(true);
    try {
      const data = await getMyCharacters();
      // 레벨 높은 순으로 정렬
      const sortedData = data.sort((a, b) => {
        const levelA = parseFloat(a.itemLevel.replace(',', ''));
        const levelB = parseFloat(b.itemLevel.replace(',', ''));
        return levelB - levelA;
      });
      setInternalCharacters(sortedData);
    } catch (error) {
      console.error('Failed to load characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/login');
  };

  const handleCharacterManagement = () => {
    navigate('/characters/update');
    setOpen(false);
  };

  if (!user) return null;

  const isAdmin = user.authorities?.includes('ADMIN');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Controlled mode (open prop provided): DialogTrigger 불필요 */}
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          {children || (
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <UserIcon className="h-5 w-5" />
              <span className="sr-only">내 정보</span>
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            내 정보
          </DialogTitle>
          <DialogDescription>
            계정 정보 및 등록된 캐릭터를 확인할 수 있습니다
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 사용자 기본 정보 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-muted-foreground">닉네임:</span>
                <span className="font-medium">{user.memberNickname}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-muted-foreground">아이디:</span>
                <span className="text-sm">{user.memberId}</span>
              </div>
            </div>
            {isAdmin && (
              <Badge variant="default" className="gap-1">
                <Shield className="h-3 w-3" />
                관리자
              </Badge>
            )}
          </div>

          <Separator />

          {/* 등록된 캐릭터 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                등록된 캐릭터
                <Badge variant="secondary" className="ml-1">
                  {characters.length}개
                </Badge>
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCharacterManagement}
              >
                캐릭터 등록
              </Button>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground">로딩 중...</p>
            ) : characters.length > 0 ? (
              <div className="space-y-2">
                {characters.map((char, index) => (
                  <div
                    key={`${char.characterId}-${index}`}
                    className="flex items-center justify-between p-2 rounded-md bg-secondary/30 hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <ClassIconSimple
                        characterClass={char.characterClass}
                        classType={char.classType as 'attacker' | 'supporter'}
                        size={32}
                      />
                      <div>
                        <p className="font-medium text-sm">{char.characterNickname}</p>
                        <p className="text-xs text-muted-foreground">
                          {char.characterClass}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">
                      Lv. {char.itemLevel}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">
                등록된 캐릭터가 없습니다
              </div>
            )}
          </div>

          <Separator />

          {/* 로그아웃 버튼 */}
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            로그아웃
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
